import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ProjectProfile } from '@kaizen/shared';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface ProjectContextType {
  projects: ProjectProfile[];
  activeProject: ProjectProfile | null;
  isLoading: boolean;
  error: string | null;
  selectProject: (projectId: string) => void;
  refreshProjects: () => Promise<void>;
  addProject: (project: Omit<ProjectProfile, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => Promise<ProjectProfile>;
  updateProject: (
    projectId: string,
    updates: Partial<Omit<ProjectProfile, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>
  ) => Promise<ProjectProfile>;
}

function mapProjectRow(row: any): ProjectProfile {
  return {
    id: row.id,
    userId: row.user_id,
    deviceId: row.device_id,
    name: row.name,
    description: row.description || undefined,
    localPath: row.local_path,
    projectType: row.project_type || 'vite',
    buildCommand: row.build_command || 'npm run build',
    testCommand: row.test_command || undefined,
    devCommand: row.dev_command || undefined,
    buildOutputDirectory: row.build_output_directory || 'dist',
    deploymentProvider: row.deployment_provider || 'cloudflare_pages',
    cloudflareProjectName: row.cloudflare_project_name || undefined,
    previewEnabled: Boolean(row.preview_enabled),
    productionEnabled: Boolean(row.production_enabled),
    productionUrl: row.production_url || undefined,
    previewUrl: row.preview_url || undefined,

    config:
      row.config ||
      undefined,

    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectProfile[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAndSeedProjects = useCallback(async () => {
    if (!isSupabaseConfigured || !user?.id) {
      setProjects([]);
      setActiveProjectId(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch user's projects from kr_projects
      const { data: existingRows, error: fetchErr } = await supabase
        .from('kr_projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (fetchErr) {
        throw new Error(`Failed to load projects: ${fetchErr.message}`);
      }

      let projectProfiles = (existingRows || []).map(mapProjectRow);

      // 2. If no project exists yet, auto-seed authoritative BCC Review Suite
      if (projectProfiles.length === 0) {
        // Find registered device
        const { data: deviceRow } = await supabase
          .from('kr_devices')
          .select('id')
          .eq('user_id', user.id)
          .order('last_seen_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (deviceRow?.id) {
          const { data: seeded, error: seedErr } = await supabase
            .from('kr_projects')
            .insert({
              user_id: user.id,
              device_id: deviceRow.id,
              name: 'BCC Review Suite',
              description: 'Enabling Work Module web client and review suite',
              local_path: 'D:\\Enabling Work Module\\kaizen-enabling-works-web',
              project_type: 'vite',
              build_command: 'npm run build',
              test_command: 'npm test',
              dev_command: 'npm run dev',
              build_output_directory: 'dist',
              deployment_provider: 'cloudflare_pages',
              cloudflare_project_name: 'lusaildpp',
              preview_enabled: true,
              production_enabled: true,
              production_url: 'https://lusaildpp.pages.dev',
              preview_url: 'https://preview.kaizen-enabling-works.pages.dev',
              config: {
                sourceControl: {
                  provider: 'github',
                  enabled: true,
                  repositoryOwner: 'Shasmeer',
                  repositoryName: 'kaizen-enabling-works-web',
                  repositoryFullName: 'Shasmeer/kaizen-enabling-works-web',
                  repositoryUrl: 'https://github.com/Shasmeer/kaizen-enabling-works-web',
                  remoteName: 'origin',
                  defaultBranch: 'main'
                }
              }
            })
            .select('*')
            .single();

          if (seedErr) {
            console.warn('Auto-seed BCC Review Suite skipped:', seedErr.message);
          } else if (seeded) {
            projectProfiles = [mapProjectRow(seeded)];
          }
        }
      }

      setProjects(projectProfiles);
      if (projectProfiles.length > 0) {
        setActiveProjectId(prev => {
          if (prev && projectProfiles.some(p => p.id === prev)) {
            return prev;
          }
          return projectProfiles[0].id;
        });
      } else {
        setActiveProjectId(null);
      }
    } catch (err: any) {
      console.error('ProjectContext error:', err);
      setError(err?.message || 'Error loading projects');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void fetchAndSeedProjects();
  }, [fetchAndSeedProjects]);

  const activeProject = projects.find(p => p.id === activeProjectId) || projects[0] || null;

  const selectProject = (id: string) => {
    setActiveProjectId(id);
  };

  const refreshProjects = async () => {
    await fetchAndSeedProjects();
  };

  const addProject = async (
    projectData: Omit<ProjectProfile, 'id' | 'createdAt' | 'updatedAt' | 'userId'>
  ): Promise<ProjectProfile> => {
    if (!user?.id) throw new Error('Authentication required to create a project');

    const { data, error: insertErr } = await supabase
      .from('kr_projects')
      .insert({
        user_id: user.id,
        device_id: projectData.deviceId,
        name: projectData.name,
        description: projectData.description || null,
        local_path: projectData.localPath,
        project_type: projectData.projectType,
        build_command: projectData.buildCommand,
        test_command: projectData.testCommand || null,
        dev_command: projectData.devCommand || null,
        build_output_directory: projectData.buildOutputDirectory,
        deployment_provider: projectData.deploymentProvider,
        cloudflare_project_name: projectData.cloudflareProjectName || null,
        preview_enabled: projectData.previewEnabled,
        production_enabled: projectData.productionEnabled,
        production_url: projectData.productionUrl || null,
        preview_url: projectData.previewUrl || null,

        config:
          projectData.config ||
          {}
      })
      .select('*')
      .single();

    if (insertErr || !data) {
      throw new Error(`Failed to create project: ${insertErr?.message || 'Unknown error'}`);
    }

    const newProfile = mapProjectRow(data);
    setProjects(prev => [...prev, newProfile]);
    setActiveProjectId(newProfile.id);
    return newProfile;
  };

  // =========================================================
  // KAIZEN_PROJECT_UPDATE_V1
  // =========================================================

  const updateProject = async (
    projectId: string,
    updates: Partial<Omit<ProjectProfile, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>
  ): Promise<ProjectProfile> => {

    if (!user?.id) {
      throw new Error('Authentication required to update a project');
    }


    const current =
      projects.find(
        project =>
          project.id === projectId
      );


    if (!current) {
      throw new Error('Project not found');
    }


    const merged: ProjectProfile = {
      ...current,
      ...updates
    };


    const {
      data,
      error: updateErr
    } =
      await supabase
        .from('kr_projects')
        .update({
          device_id:
            merged.deviceId,

          name:
            merged.name,

          description:
            merged.description || null,

          local_path:
            merged.localPath,

          project_type:
            merged.projectType,

          build_command:
            merged.buildCommand,

          test_command:
            merged.testCommand || null,

          dev_command:
            merged.devCommand || null,

          build_output_directory:
            merged.buildOutputDirectory,

          deployment_provider:
            merged.deploymentProvider,

          cloudflare_project_name:
            merged.cloudflareProjectName || null,

          preview_enabled:
            merged.previewEnabled,

          production_enabled:
            merged.productionEnabled,

          production_url:
            merged.productionUrl || null,

          preview_url:
            merged.previewUrl || null,

          config:
            merged.config ||
            {}
        })
        .eq(
          'id',
          projectId
        )
        .eq(
          'user_id',
          user.id
        )
        .select('*')
        .single();


    if (
      updateErr ||
      !data
    ) {

      throw new Error(
        `Failed to update project: ${updateErr?.message || 'Unknown error'}`
      );
    }


    const updated =
      mapProjectRow(
        data
      );


    setProjects(
      previous =>
        previous.map(
          project =>
            project.id === updated.id
              ? updated
              : project
        )
    );


    return updated;
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProject,
        isLoading,
        error,
        selectProject,
        refreshProjects,
        addProject,
        updateProject
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = (): ProjectContextType => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjects must be used within a ProjectProvider');
  return ctx;
};
